import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LuEye, LuEyeOff, LuLoaderCircle, LuCircleAlert } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import { apiFetch } from '../services/api';

/**
 * /redefinir-senha?token=… — o destino do link do e-mail.
 *
 * Anônima: a pessoa pode abrir o e-mail em outro navegador. Ao salvar, o back
 * derruba as sessões abertas (o refresh token é revogado) — se a senha está
 * sendo trocada porque alguém entrou, deixar a sessão dele viva anula a troca.
 * Por isso aqui não há login automático: a nova senha é usada uma vez, no login.
 */
const RedefinirSenhaPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /* Mesma regra do back (Senha.ValidarComplexidade) e mesma frase do cadastro. */
  const temTamanho = senha.length >= 8;
  const temMaiuscula = /[A-Z]/.test(senha);
  const temMinuscula = /[a-z]/.test(senha);
  const temEspecial = /[^A-Za-z0-9]/.test(senha);
  const criteriosOk = [temTamanho, temMaiuscula, temMinuscula, temEspecial].filter(Boolean).length;
  const senhaValida = criteriosOk === 4;

  const ajuda = (): string => {
    if (!senha) return 'Mínimo de 8 caracteres, com maiúscula, minúscula e um símbolo.';
    if (senhaValida) return 'Boa senha.';
    const faltando: string[] = [];
    if (!temTamanho) faltando.push('8 caracteres');
    if (!temMaiuscula) faltando.push('uma maiúscula');
    if (!temMinuscula) faltando.push('uma minúscula');
    if (!temEspecial) faltando.push('um símbolo');
    const lista = faltando.length === 1
      ? faltando[0]
      : `${faltando.slice(0, -1).join(', ')} e ${faltando[faltando.length - 1]}`;
    return `Falta ${lista}.`;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (carregando) return;

    if (!senhaValida) {
      setErro(ajuda());
      document.getElementById('red-senha')?.focus();
      return;
    }

    setErro(null);
    setCarregando(true);

    try {
      const resposta = await apiFetch(`${apiUrl}/api/Auth/RedefinirSenha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, novaSenha: senha }),
      });
      const dados = await resposta.json().catch(() => ({}));

      if (!resposta.ok || dados.sucesso === false) {
        setErro(dados.mensagem || dados.message || 'Este link não é mais válido.');
        return;
      }

      navigate('/login?senha=1', { replace: true });
    } catch {
      setErro('Não conseguimos falar com o servidor. Tente de novo em instantes.');
    } finally {
      setCarregando(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <h1 className="au-h1">Link inválido</h1>
        <p className="au-sub">Este endereço não tem o código de redefinição.</p>
        <Link to="/esqueci-senha" className="au-btn" style={{ textDecoration: 'none' }}>
          Pedir um link novo
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">Criar uma nova senha</h1>
      <p className="au-sub">Depois de salvar, entre com ela.</p>

      <form onSubmit={handleSubmit} noValidate>
        {erro && (
          <div className="au-alert is-danger" role="alert">
            <LuCircleAlert size={16} />
            <span>{erro}</span>
          </div>
        )}

        <div className="au-group">
          <label className="au-label" htmlFor="red-senha">Nova senha</label>
          <div className="au-input-wrap has-toggle">
            <input
              id="red-senha"
              className={`au-input${erro ? ' is-error' : ''}`}
              type={mostrarSenha ? 'text' : 'password'}
              placeholder="••••••••"
              value={senha}
              autoComplete="new-password"
              autoFocus
              onChange={ev => { setSenha(ev.target.value); if (erro) setErro(null); }}
            />
            <button
              type="button"
              className="au-toggle"
              onClick={() => setMostrarSenha(v => !v)}
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {mostrarSenha ? <LuEyeOff size={16} /> : <LuEye size={16} />}
            </button>
          </div>
          <div className="au-meter" aria-hidden="true">
            {[0, 1, 2, 3].map(i => (
              <span
                key={i}
                className={criteriosOk > i ? (criteriosOk === 1 && senha ? 'is-weak' : 'is-on') : ''}
              />
            ))}
          </div>
          <p className="au-help">{ajuda()}</p>
        </div>

        <button type="submit" className={`au-btn${carregando ? ' is-loading' : ''}`}>
          {carregando ? (
            <><LuLoaderCircle size={16} className="au-spin" /> Salvando…</>
          ) : (
            'Salvar e entrar'
          )}
        </button>

        <p className="au-help" style={{ textAlign: 'center', marginTop: 12 }}>
          Ao salvar, as sessões abertas em outros aparelhos são encerradas.
        </p>
      </form>
    </AuthShell>
  );
};

export default RedefinirSenhaPage;
